import { forwardRef, type JSX } from 'react';
import { useIntl } from 'react-intl';

import {
  ChevronLeftSmallIcon,
  ChevronRightSmallIcon,
} from '@dynatrace/strato-icons';

import { Button } from '../../../buttons/button/Button.js';
import { Flex } from '../../../layouts/flex/Flex.js';
import { useFormFieldContext } from '../../forms-core/hooks/useFormFieldContext.js';
import { SelectDateLabel } from '../calendar-header-label/SelectDateLabel.js';
import { SelectMonthLabel } from '../calendar-header-label/SelectMonthLabel.js';
import { SelectYearLabel } from '../calendar-header-label/SelectYearLabel.js';
import * as itemStyles from '../components/CalendarItem.sty.js';
import { useCalendarContext } from '../hooks/useCalendarContext.js';
import { NUMBER_OF_YEARS } from '../types/calendarConfiguration.js';
import type { CalendarViewType } from '../types/shared-types.js';

/**
 * @internal
 */
interface CalendarHeaderProps {
  /** The view currently shown to provide the proper text content. */
  view: CalendarViewType;
  /** Calendar header label id used for the 'aria-labelledby' attribute. */
  labelId: string;
}

/**
 * Returns the right values for label, nextView, data-dt-name for the header button,
 * and the navigation button payload for each view.
 */
function getViewSpecificProps(view: CalendarViewType): {
  label: JSX.Element;
  nextView: CalendarViewType;
  headerDataDtProperties: string;
  payload: { amount: number; unit?: CalendarViewType };
} {
  switch (view) {
    case 'day': {
      const nextView = 'month';
      return {
        label: <SelectDateLabel />,
        nextView,
        headerDataDtProperties: `value:${nextView}`,
        payload: { amount: 1, unit: nextView },
      };
    }
    case 'month': {
      const nextView = 'year';
      return {
        label: <SelectMonthLabel />,
        nextView,
        headerDataDtProperties: `value:${nextView}`,
        payload: { amount: 1, unit: nextView },
      };
    }
    case 'year': {
      const nextView = 'day';
      return {
        label: <SelectYearLabel />,
        nextView,
        headerDataDtProperties: `value:${nextView}`,
        payload: { amount: NUMBER_OF_YEARS },
      };
    }
  }
}

/**
 * CalendarHeader component
 * Renders the calendar header, including the buttons to navigate to the previous/next
 * month/year/list of years and the button (label) to switch the calendar view.
 * @internal
 */
export const CalendarHeader = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  CalendarHeaderProps
>((props: CalendarHeaderProps, forwardedRef) => {
  const { labelId, view } = props;

  const { dispatch } = useCalendarContext();
  const { disabled, ariaDisabled } = useFormFieldContext();
  const intl = useIntl();

  const { label, nextView, payload, headerDataDtProperties } =
    getViewSpecificProps(view);

  return (
    <Flex justifyContent="space-between" marginBottom={16}>
      <Button
        disabled={disabled}
        aria-disabled={ariaDisabled}
        aria-label={intl.formatMessage({
          defaultMessage: 'Go to previous page.',
          id: 'VoqQWp9yEM0QM8eF',
          description: 'Aria label to add to the `previous` button',
        })}
        onClick={() =>
          dispatch({
            type: 'navigateCurrentView',
            payload: { ...payload, amount: payload.amount * -1 },
          })
        }
        data-dt-name="PreviousPage"
      >
        <Button.Prefix>
          <ChevronLeftSmallIcon />
        </Button.Prefix>
      </Button>
      <Button
        className={itemStyles.headerButton}
        disabled={disabled}
        aria-disabled={ariaDisabled}
        aria-live="polite"
        id={labelId}
        onClick={() =>
          dispatch({
            type: 'currentViewChange',
            payload: nextView,
          })
        }
        ref={forwardedRef}
        data-dt-name="ChangeView"
        data-dt-properties={headerDataDtProperties}
      >
        {label}
      </Button>
      <Button
        disabled={disabled}
        aria-disabled={ariaDisabled}
        aria-label={intl.formatMessage({
          defaultMessage: 'Go to next page.',
          id: 'KAAzM9PVtiJno9ks',
          description: 'Aria label to add to the `next` button',
        })}
        onClick={() =>
          dispatch({
            type: 'navigateCurrentView',
            payload,
          })
        }
        data-dt-name="NextPage"
      >
        <Button.Prefix>
          <ChevronRightSmallIcon />
        </Button.Prefix>
      </Button>
    </Flex>
  );
});

(
  CalendarHeader as typeof CalendarHeader & { displayName: string }
).displayName = 'CalendarHeader';
